import { useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/shared/empty-state";
import { TableSkeleton } from "@/components/shared/loading-skeleton";
import { toastMutationError } from "@/lib/utils/formatting";
import { useSyncTenantDirectoryUsersMutation } from "@/lib/query/tenant-app-users";
import { formatTenantAppUserRoleLabels } from "../../../../../packages/shared-types/src/tenant-app-user-roles.js";
import { TenantAppUserEditorDialog } from "./tenant-app-user-editor-dialog.js";
import type { User } from "@/lib/api/generated/control-api.schemas";

export function UsersSection({
  tenantId,
  users,
  isLoading,
  errorMessage,
}: {
  tenantId: string;
  users: User[];
  isLoading: boolean;
  errorMessage?: string;
}) {
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const syncMutation = useSyncTenantDirectoryUsersMutation();

  async function handleSyncUsers() {
    try {
      const result = await syncMutation.mutateAsync({ tenantId });
      toast.success(
        `Synced ${result.totalCount} users (${result.createdCount} new, ${result.updatedCount} updated, ${result.deletedCount} removed).`,
      );
    } catch (error) {
      toast.error(toastMutationError(error, "Failed to sync users"));
    }
  }

  return (
    <>
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-3">
          <div className="space-y-1.5">
            <CardTitle>Assigned Users</CardTitle>
            <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
              These are the Agentiq users currently assigned to this tenant.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleSyncUsers}
            disabled={syncMutation.isPending || !tenantId}
          >
            <RefreshCw size={14} className={syncMutation.isPending ? "animate-spin" : ""} />
            Sync
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <TableSkeleton rows={4} cols={7} />
          ) : errorMessage ? (
            <EmptyState title="Assigned users unavailable" description={errorMessage} />
          ) : users.length === 0 ? (
            <EmptyState
              title="No assigned users"
              description="No Agentiq users are currently assigned to this tenant."
            />
          ) : (
            <div className="border border-border rounded-md overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr style={{ background: "var(--bg-accent)" }}>
                    <HeaderCell>Name</HeaderCell>
                    <HeaderCell>Email</HeaderCell>
                    <HeaderCell>Phone</HeaderCell>
                    <HeaderCell>Status</HeaderCell>
                    <HeaderCell>Roles</HeaderCell>
                    <HeaderCell>User ID</HeaderCell>
                    <HeaderCell>Actions</HeaderCell>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="border-t border-border">
                      <BodyCell strong>{user.displayName || user.email || user.id}</BodyCell>
                      <BodyCell>{user.email}</BodyCell>
                      <BodyCell>{user.phone ?? "-"}</BodyCell>
                      <BodyCell>{formatUserStatus(user)}</BodyCell>
                      <BodyCell>
                        {user.roleKeys && user.roleKeys.length > 0
                          ? formatTenantAppUserRoleLabels(user.roleKeys).join(", ")
                          : "-"}
                      </BodyCell>
                      <BodyCell mono>{user.id}</BodyCell>
                      <BodyCell>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingUser(user)}
                        >
                          Edit
                        </Button>
                      </BodyCell>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      <TenantAppUserEditorDialog
        tenantId={tenantId}
        user={editingUser}
        onOpenChange={(open) => {
          if (!open) {
            setEditingUser(null);
          }
        }}
      />
    </>
  );
}

function formatUserStatus(user: User) {
  if (!user.isIdentityActive || !user.isApplicationAssignmentActive) {
    return "Inactive";
  }
  if (user.phone && !user.phoneConfirmed) {
    return "Join pending";
  }
  return "Ready";
}

function HeaderCell({ children }: { children: React.ReactNode }) {
  return (
    <th
      className="text-left px-4 py-2 font-medium"
      style={{ color: "var(--muted-foreground)" }}
    >
      {children}
    </th>
  );
}

function BodyCell({
  children,
  strong,
  mono,
}: {
  children: React.ReactNode;
  strong?: boolean;
  mono?: boolean;
}) {
  return (
    <td
      className={`px-4 py-2.5 ${strong ? "font-medium" : ""} ${mono ? "font-mono" : ""}`}
      style={{ color: strong ? "var(--text-strong)" : "var(--muted-foreground)" }}
    >
      {children}
    </td>
  );
}
